import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, ArrowRight, Sparkles } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YOUSE_LOGO = 'https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png';

/**
 * Confetti-like animated sparkles that float behind the campaign card.
 */
const SparkleField = ({ accent }) => {
  const sparkles = Array.from({ length: 18 });
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {sparkles.map((_, i) => {
        const delay = (i * 0.35) % 4;
        const x = (i * 53) % 100;
        const y = (i * 71) % 100;
        const size = 4 + (i % 5) * 2;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0], y: [0, -22, 0] }}
            transition={{ duration: 3.2, delay, repeat: Infinity, repeatType: 'loop' }}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: '50%',
              background: accent,
              filter: 'blur(0.5px)',
              boxShadow: `0 0 ${size * 2}px ${accent}`,
            }}
          />
        );
      })}
    </div>
  );
};

/**
 * MarketingCampaignPopup
 * Richmedia animated popup shown to authenticated users.
 *  - Fetches /api/marketing/active-campaigns once on mount
 *  - Shows the highest-priority campaign that the user hasn't dismissed yet
 *    (dismissals persisted in localStorage keyed by campaign id)
 *  - Tracks view / dismiss / click events to the backend
 */
const MarketingCampaignPopup = () => {
  const [campaign, setCampaign] = useState(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const viewedRef = useRef(false);

  const dismissKey = (id) => `youse_marketing_dismissed_${id}`;

  const track = useCallback(async (event, id) => {
    try {
      await axios.post(`${API_URL}/marketing/campaigns/${id}/track`, { event });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchCampaigns = async () => {
      try {
        const r = await axios.get(`${API_URL}/marketing/active-campaigns`);
        if (cancelled) return;
        const list = r.data?.campaigns || [];
        const next = list.find((c) => {
          if (!c?.id) return false;
          if (c.dismissible === false) return true;
          return !localStorage.getItem(dismissKey(c.id));
        });
        if (next) {
          setCampaign(next);
          setTimeout(() => setVisible(true), 700);
        }
      } catch { /* ignore */ }
    };
    fetchCampaigns();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (visible && campaign?.id && !viewedRef.current) {
      viewedRef.current = true;
      track('view', campaign.id);
    }
  }, [visible, campaign, track]);

  const handleDismiss = () => {
    if (!campaign) return;
    if (campaign.dismissible !== false) {
      localStorage.setItem(dismissKey(campaign.id), '1');
    }
    track('dismiss', campaign.id);
    setVisible(false);
  };

  const handleClick = () => {
    if (!campaign?.cta_url) return;
    track('click', campaign.id);
    localStorage.setItem(dismissKey(campaign.id), '1');
    setVisible(false);
    try {
      if (/^https?:\/\//i.test(campaign.cta_url)) {
        const u = new URL(campaign.cta_url);
        if (u.origin === window.location.origin) {
          navigate(u.pathname + u.search + u.hash);
          return;
        }
        window.location.href = campaign.cta_url;
        return;
      }
      navigate(campaign.cta_url);
    } catch {
      navigate(campaign.cta_url);
    }
  };

  if (!campaign) return null;
  const accent = campaign.accent_color || '#22c55e';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleDismiss}
          data-testid="marketing-popup-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 99998,
            background: 'rgba(0,0,0,0.78)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            pointerEvents: visible ? 'auto' : 'none',
          }}
        >
          <motion.div
            initial={{ y: 60, scale: 0.85, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 60, scale: 0.85, opacity: 0 }}
            transition={{ type: 'spring', damping: 18, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="marketing-popup-card"
            style={{
              position: 'relative',
              width: '100%', maxWidth: '480px',
              borderRadius: '24px',
              overflow: 'hidden',
              background: `linear-gradient(160deg, #0f1116 0%, #131720 100%)`,
              border: `1px solid ${accent}55`,
              boxShadow: `0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px ${accent}22, 0 0 80px ${accent}40`,
              color: '#fff',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            <SparkleField accent={accent} />

            {/* Accent ribbon */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
              background: `linear-gradient(90deg, ${accent}, #ffffff80, ${accent})`,
              backgroundSize: '200% 100%',
              animation: 'youse-ribbon-shimmer 3s linear infinite',
            }} />

            {/* Close */}
            {campaign.dismissible !== false && (
              <button
                onClick={handleDismiss}
                aria-label="Close"
                data-testid="marketing-popup-close"
                style={{
                  position: 'absolute', top: '14px', right: '14px',
                  background: 'rgba(255,255,255,0.08)', border: 'none',
                  borderRadius: '50%', width: '34px', height: '34px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'white', zIndex: 2,
                }}
              >
                <X size={16} />
              </button>
            )}

            <div style={{ position: 'relative', padding: '28px 28px 8px 28px', textAlign: 'center', zIndex: 1 }}>
              <motion.img
                src={YOUSE_LOGO} alt="Youse Capital"
                initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }}
                style={{ height: '72px', width: 'auto', margin: '0 auto', filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))' }}
              />
            </div>

            {campaign.image_url && (
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                style={{ position: 'relative', padding: '0 24px', zIndex: 1 }}
              >
                <img src={campaign.image_url} alt="" style={{ width: '100%', height: 'auto', borderRadius: '14px' }} />
              </motion.div>
            )}

            <div style={{ position: 'relative', padding: '24px 28px 28px 28px', zIndex: 1 }}>
              {campaign.subtitle && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 12px', borderRadius: '999px',
                    background: `${accent}1f`, border: `1px solid ${accent}55`,
                    color: accent, fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                    textTransform: 'uppercase', marginBottom: '12px',
                  }}
                >
                  <Sparkles size={12} /> {campaign.subtitle}
                </motion.div>
              )}

              <motion.h2
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', fontWeight: 800, lineHeight: 1.2, marginBottom: '10px' }}
                data-testid="marketing-popup-title"
              >
                {campaign.title}
              </motion.h2>

              {campaign.body && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                  style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: 1.55, marginBottom: '20px' }}
                  data-testid="marketing-popup-body"
                >
                  {campaign.body}
                </motion.p>
              )}

              {campaign.cta_url && campaign.cta_label && (
                <motion.button
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
                  onClick={handleClick}
                  data-testid="marketing-popup-cta"
                  style={{
                    width: '100%', padding: '14px 22px', borderRadius: '14px',
                    border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
                    color: '#0a0d14', fontWeight: 800, fontSize: '15px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: `0 10px 30px ${accent}55`,
                  }}
                >
                  {campaign.cta_label} <ArrowRight size={16} />
                </motion.button>
              )}
            </div>

            <style>{`
              @keyframes youse-ribbon-shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MarketingCampaignPopup;
